/**
 * Purpose: Per-player report card showing class-by-class grades, percentages,
 *          GPA points, overall GPA, and honor title.
 * Owns: Grade table rendering, GPA summary, honor title display for a single player.
 * Notes: Uses existing engine outputs (calculateClassPercentage, getHonorTitle) — no
 *        grading logic lives here. Designed for the end-of-game Results screen.
 *        All colors use theme tokens (CS-22). No magic numbers (CS-20).
 */

import type { Player } from '../../types/game';
import type { AgeGroup } from '../../types/question';
import { calculateClassPercentage } from '../../engine/scoring';
import { getHonorTitle } from '../../engine/scoring';
import { GRADING_SCALE } from '../../config/classConfig';

// _______________________________________________
//              PROPS
// _______________________________________________

interface ReportCardProps {
  /** The player whose report card to display */
  player: Player;
  /** Optional ranking label (e.g., "Valedictorian", "Salutatorian") */
  rankingLabel?: string;
  /** Optional GPA override — used in Race mode to display a fair GPA that
   *  accounts for incomplete classes (see calculateFinalGPA in scoring.ts). */
  overrideGpa?: number;
}

// _______________________________________________
//              HELPERS
// _______________________________________________

/** Look up GPA points for a letter grade from the grading scale config. */
function getGpaPointsForLetter(letter: string): number {
  const entry = GRADING_SCALE.find((g) => g.letter === letter);
  return entry?.gpaPoints ?? 0;
}

/** Format a percentage to one decimal place for display. */
function formatPercent(value: number): string {
  return `${Math.round(value * 10) / 10}%`;
}

/** Format GPA to two decimal places. */
function formatGpa(gpa: number): string {
  return gpa.toFixed(2);
}

// _______________________________________________
//              COMPONENT
// _______________________________________________

export function ReportCard({ player, rankingLabel, overrideGpa }: ReportCardProps) {
  const displayGpa = overrideGpa ?? player.gpa;
  const honorTitle = getHonorTitle(displayGpa);

  return (
    <div style={styles.card} role="region" aria-label={`Report card for ${player.name}`}>
      {/* Player header */}
      <div style={styles.header}>
        <div style={{ ...styles.avatar, backgroundColor: player.color }}>
          {player.name.charAt(0).toUpperCase()}
        </div>
        <div style={styles.headerInfo}>
          <span style={styles.playerName}>{player.name}</span>
          {rankingLabel && (
            <span style={styles.rankingLabel}>{rankingLabel}</span>
          )}
        </div>
        <div style={styles.gpaBlock}>
          <span style={styles.gpaValue}>{formatGpa(displayGpa)}</span>
          <span style={styles.gpaLabel}>GPA</span>
        </div>
      </div>

      {/* Honor title */}
      <div style={styles.honorRow}>
        <span style={styles.honorTitle}>{honorTitle}</span>
      </div>

      {/* Class-by-class grade table */}
      <div style={styles.gradeTable} role="table" aria-label="Class grades">
        {/* Table header */}
        <div style={styles.tableHeaderRow} role="row">
          <span style={{ ...styles.tableHeaderCell, ...styles.classCol }} role="columnheader">
            Class
          </span>
          <span style={{ ...styles.tableHeaderCell, ...styles.gradeCol }} role="columnheader">
            Grade
          </span>
          <span style={{ ...styles.tableHeaderCell, ...styles.pctCol }} role="columnheader">
            %
          </span>
          <span style={{ ...styles.tableHeaderCell, ...styles.gpaCol }} role="columnheader">
            GPA
          </span>
        </div>

        {/* One row per class period */}
        {player.schedule.map((period, index) => {
          const progress = player.classProgress[index];
          const isPassed = progress?.passed ?? false;
          const letterGrade = progress?.letterGrade ?? null;

          /* Calculate percentage for display using the existing engine formula */
          const percentage = isPassed
            ? calculateClassPercentage(
                progress.questionsAnswered,
                progress.questionsCorrect,
                progress.questionsWithHelp,
                progress.behaviorDock,
                player.ageGroup as AgeGroup,
              )
            : null;

          const gpaPoints = letterGrade ? getGpaPointsForLetter(letterGrade) : null;

          return (
            <div
              key={period.periodNumber}
              style={{
                ...styles.tableRow,
                ...(index % 2 === 0 ? styles.tableRowEven : {}),
              }}
              role="row"
            >
              <span style={{ ...styles.tableCell, ...styles.classCol }} role="cell">
                {period.subject}
              </span>
              <span
                style={{
                  ...styles.tableCell,
                  ...styles.gradeCol,
                  ...styles.gradeText,
                  ...(isPassed ? getGradeColorStyle(letterGrade) : styles.pendingText),
                }}
                role="cell"
              >
                {isPassed ? letterGrade : '--'}
              </span>
              <span style={{ ...styles.tableCell, ...styles.pctCol }} role="cell">
                {percentage !== null ? formatPercent(percentage) : '--'}
              </span>
              <span style={{ ...styles.tableCell, ...styles.gpaCol }} role="cell">
                {gpaPoints !== null ? gpaPoints.toFixed(1) : '--'}
              </span>
            </div>
          );
        })}
      </div>

      {/* Summary row */}
      <div style={styles.summaryRow}>
        <span style={styles.summaryLabel}>Overall GPA</span>
        <span style={styles.summaryValue}>{formatGpa(displayGpa)} / 4.00</span>
      </div>

      {/* Quick stats */}
      <div style={styles.statsRow}>
        <span style={styles.stat}>
          {player.questionsCorrect}/{player.questionsCorrect + player.questionsWrong} correct
        </span>
        {player.bestStreak > 1 && (
          <span style={styles.stat}>Best streak: {player.bestStreak}</span>
        )}
        <span style={styles.stat}>Score: {player.score}</span>
      </div>
    </div>
  );
}

// _______________________________________________
//              GRADE COLOR HELPER
// _______________________________________________

/** Returns a style object with a color appropriate for the letter grade. */
function getGradeColorStyle(letterGrade: string | null): React.CSSProperties {
  switch (letterGrade) {
    case 'A':
      return { color: 'var(--correct)' };
    case 'B':
      return { color: 'var(--accent-gold)' };
    case 'C':
      return { color: 'var(--text-light)' };
    case 'D':
      return { color: 'var(--btn-b)' };
    case 'F':
      return { color: 'var(--incorrect)' };
    default:
      return {};
  }
}

// _______________________________________________
//              STYLES
// _______________________________________________

const styles: Record<string, React.CSSProperties> = {
  card: {
    backgroundColor: 'var(--bg-surface)',
    borderRadius: '12px',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  },

  /* Header: avatar + name + GPA block */
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1rem',
    fontWeight: '700',
    color: 'var(--text-light)',
    flexShrink: 0,
  },
  headerInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  playerName: {
    fontSize: '1.15rem',
    fontWeight: '700',
    color: 'var(--text-light)',
  },
  rankingLabel: {
    fontSize: '0.8rem',
    fontWeight: '600',
    color: 'var(--accent-gold)',
    fontStyle: 'italic',
  },
  gpaBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0.35rem 0.6rem',
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderRadius: '8px',
  },
  gpaValue: {
    fontSize: '1.4rem',
    fontWeight: '800',
    color: 'var(--accent-gold)',
    fontFamily: 'var(--font-family)',
    lineHeight: '1.1',
  },
  gpaLabel: {
    fontSize: '0.6rem',
    fontWeight: '600',
    color: 'var(--muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
  },

  /* Honor title */
  honorRow: {
    textAlign: 'center' as const,
    padding: '0.25rem 0',
  },
  honorTitle: {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: 'var(--accent-gold)',
    fontFamily: 'var(--font-family-handwriting)',
    letterSpacing: '0.02em',
  },

  /* Grade table */
  gradeTable: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '8px',
    overflow: 'hidden',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  tableHeaderRow: {
    display: 'flex',
    backgroundColor: 'rgba(255,255,255,0.08)',
    padding: '0.4rem 0.5rem',
  },
  tableHeaderCell: {
    fontSize: '0.65rem',
    fontWeight: '700',
    color: 'var(--muted)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  },
  tableRow: {
    display: 'flex',
    padding: '0.4rem 0.5rem',
  },
  tableRowEven: {
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  tableCell: {
    fontSize: '0.85rem',
    color: 'var(--text-light)',
  },

  /* Column widths — flex-based for responsive layout */
  classCol: {
    flex: 2,
    minWidth: 0,
  },
  gradeCol: {
    flex: 0.8,
    textAlign: 'center' as const,
  },
  pctCol: {
    flex: 1,
    textAlign: 'center' as const,
  },
  gpaCol: {
    flex: 0.8,
    textAlign: 'center' as const,
  },

  /* Grade text styling */
  gradeText: {
    fontWeight: '700',
  },
  pendingText: {
    color: 'var(--muted)',
    fontStyle: 'italic',
  },

  /* Summary row */
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.5rem 0.5rem 0',
    borderTop: '1px solid rgba(255,255,255,0.08)',
  },
  summaryLabel: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: 'var(--text-light)',
  },
  summaryValue: {
    fontSize: '1rem',
    fontWeight: '800',
    color: 'var(--accent-gold)',
    fontFamily: 'var(--font-family)',
  },

  /* Quick stats */
  statsRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.75rem',
    paddingTop: '0.25rem',
  },
  stat: {
    fontSize: '0.75rem',
    color: 'var(--muted)',
  },
};
